import type { Ref } from "vue";
import { readonly, ref } from "vue";
import { z } from "zod";

const RepositorySchema = z.object({
  owner: z.string(),
  repository: z.string(),
  used: z.number(),
});

const AccountSchema = z.object({
  server: z.string(),
  token_name: z.string(),
  token: z.string(),
  username: z.string(),
  uid: z.number(),
  enabled: z.boolean(),
  repositories: z.array(RepositorySchema),
});

export type Account = z.infer<typeof AccountSchema>;
export type AccountRepository = z.infer<typeof RepositorySchema>;

const key = "accounts";

function readAccounts(): Account[] {
  const item = localStorage.getItem(key);
  if (!item) {
    return [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(item);
  } catch (error) {
    console.error("Cannot parse JSON", item, error);
    return [];
  }
  const result = z.array(AccountSchema).safeParse(parsed);
  if (!result.success) {
    console.error("Invalid data in localStorage:", result.error);
    return [];
  }
  return result.data;
}

function writeAccounts(accounts: Account[]) {
  try {
    const parsed = z.array(AccountSchema).parse(accounts);
    localStorage.setItem(key, JSON.stringify(parsed));
  } catch (error) {
    console.error("Validation failed:", error);
  }
}

export function setActiveAccount(server: string | undefined) {
  account.value = accounts.value.find((a) => a.server === server);
}

export function setAccount(newAccount: Account | undefined) {
  console.log("setAccount");
  if (!newAccount) {
    account.value = undefined;
    return;
  }
  const newAccounts = readAccounts().filter(
    (t) => t.server !== newAccount.server,
  );
  newAccounts.push(newAccount);
  if (newAccount.server === window.location.host) {
    localToken.value = newAccount;
  } else {
    account.value = newAccount;
  }
  accounts.value = newAccounts;
  writeAccounts(accounts.value);
}

export function addActiveRepository(server: string, newRepo: string) {
  const serverAccount = accounts.value.find((a) => a.server === server);
  if (!serverAccount) {
    return;
  }
  const [owner, repository] = newRepo.split("/");
  if (!owner || !repository) {
    return;
  }
  const existing = serverAccount.repositories.find((r) => {
    return r.owner === owner && r.repository === repository;
  });
  if (!existing) {
    serverAccount.repositories.push({ owner, repository, used: 0 });
    serverAccount.repositories.sort(
      (a, b) =>
        a.owner.localeCompare(b.owner) ||
        a.repository.localeCompare(b.repository),
    );
    writeAccounts(accounts.value);
  }
}
export function removeActiveRepository(server: string, newRepo: string) {
  const serverAccount = accounts.value.find((a) => a.server === server);
  if (!serverAccount) {
    return;
  }
  const [owner, repository] = newRepo.split("/");
  if (!owner || !repository) {
    return;
  }
  const existing = serverAccount.repositories.findIndex(
    (r) => r.owner === owner && r.repository === repository,
  );
  if (existing !== -1) {
    serverAccount.repositories.splice(existing, 1);
    writeAccounts(accounts.value);
  }
}

const account: Ref<Account | undefined> = ref();
export const currentAccount = readonly(account);

const accounts: Ref<Account[]> = ref([]);
export const currentAccounts = readonly(accounts);

const localToken: Ref<Account | undefined> = ref();
export const localAccount = readonly(localToken);

function init() {
  accounts.value = readAccounts();
  for (const a of accounts.value) {
    if (a.server === window.location.host) {
      localToken.value = a;
    } else {
      account.value ??= a;
    }
  }
}
init();
